'use client';

import Link from 'next/link';
import { useComponentTranslations } from '@/app/components/LanguageProvider';
import { useLocale } from '@/app/components/LocaleProvider';
import { getPathWithLocale } from '@/lib/i18n';

export default function ReferralRulesClient() {
  const { t } = useComponentTranslations('Referral');
  const locale = useLocale();

  return (
    <main className="min-h-screen bg-transparent text-white pt-24 md:pt-32 pb-20 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-10">
          <h1 className="text-3xl md:text-5xl font-bold text-white">
            {t('rulesPageTitle', 'Правила реферальной программы')}
          </h1>
          <p className="mt-3 text-neutral-300">
            {t('rulesPageSubtitle', 'Условия участия в партнёрской программе Dev48')}
          </p>
        </div>

        <div className="space-y-8">
          <section className="p-6 md:p-8 rounded-2xl bg-gray-900/80 border border-white/10 backdrop-blur-2xl">
            <h2 className="text-xl md:text-2xl font-semibold text-white mb-4">
              {t('rulesSectionGeneral', '1. Общие положения')}
            </h2>
            <ul className="list-disc list-inside space-y-2 text-neutral-300">
              <li>{t('rulesGeneral1', 'Реферальная программа доступна для всех зарегистрированных пользователей Dev48.')}</li>
              <li>{t('rulesGeneral2', 'Участие в программе бесплатное и не требует дополнительных взносов.')}</li>
              <li>{t('rulesGeneral3', 'Dev48 оставляет за собой право изменять условия программы с уведомлением участников за 30 дней.')}</li>
              <li>{t('rulesGeneral4', 'Участник подтверждает, что ознакомился с настоящими правилами и принимает их в полном объёме.')}</li>
              <li>{t('rulesGeneral5', 'В случае расхождений приоритет имеют данные учёта Dev48 (клики, регистрации, статусы выплат).')}</li>
            </ul>
          </section>

          <section className="p-6 md:p-8 rounded-2xl bg-gray-900/80 border border-white/10 backdrop-blur-2xl">
            <h2 className="text-xl md:text-2xl font-semibold text-white mb-4">
              {t('rulesSectionReferral', '2. Условия привлечения')}
            </h2>
            <ul className="list-disc list-inside space-y-2 text-neutral-300">
              <li>{t('rulesReferral1', 'Рефералом считается новый клиент, который ранее не имел договорных отношений с Dev48.')}</li>
              <li>{t('rulesReferral2', 'Привлечённые клиенты должны быть реальными юридическими или физическими лицами.')}</li>
              <li>{t('rulesReferral3', 'Запрещается использование спам-рассылок, мошеннических схем и других недобросовестных методов привлечения.')}</li>
              <li>{t('rulesReferral4', 'Запрещена реклама с вводящими в заблуждение обещаниями (цены, сроки, гарантии) от имени Dev48.')}</li>
              <li>{t('rulesReferral5', 'Dev48 вправе отказать в признании реферала при наличии признаков фрода или нарушения законодательства/этики.')}</li>
            </ul>
          </section>

          <section className="p-6 md:p-8 rounded-2xl bg-gray-900/80 border border-white/10 backdrop-blur-2xl">
            <h2 className="text-xl md:text-2xl font-semibold text-white mb-4">
              {t('rulesSectionCommissions', '3. Комиссионные вознаграждения')}
            </h2>
            <ul className="list-disc list-inside space-y-2 text-neutral-300">
              <li>{t('rulesCommissions1', 'Размер комиссии зависит от суммы заключённого контракта (от 2% до 7%).')}</li>
              <li>{t('rulesCommissions2', 'Выплата производится после полного поступления оплаты от клиента.')}</li>
              <li>{t('rulesCommissions3', 'Период холда для верификации транзакции составляет 30 дней.')}</li>
              <li>{t('rulesCommissions4', 'Комиссия начисляется только по фактически оплаченной части работ/услуг и может корректироваться при возвратах/отменах.')}</li>
              <li>{t('rulesCommissions5', 'Dev48 не гарантирует начисление комиссии в случае технических сбоев у третьих сторон (браузер, блокировщики, провайдер).')}</li>
            </ul>
          </section>

          <section className="p-6 md:p-8 rounded-2xl bg-gray-900/80 border border-white/10 backdrop-blur-2xl">
            <h2 className="text-xl md:text-2xl font-semibold text-white mb-4">
              {t('rulesSectionRestrictions', '4. Ограничения')}
            </h2>
            <ul className="list-disc list-inside space-y-2 text-neutral-300">
              <li>{t('rulesRestrictions1', 'Самореферал (регистрация через собственную ссылку) запрещён и не приносит вознаграждения.')}</li>
              <li>{t('rulesRestrictions2', 'Нельзя привлекать клиентов, которые уже находятся в процессе переговоров с Dev48.')}</li>
              <li>{t('rulesRestrictions3', 'При нарушении правил аффилиат может быть отключён от программы без выплаты накопленных средств.')}</li>
              <li>{t('rulesRestrictions4', 'Запрещено использовать бренд Dev48 в доменах, названиях аккаунтов и рекламе так, чтобы создавать впечатление официального представительства без согласования.')}</li>
              <li>{t('rulesRestrictions5', 'Dev48 может приостановить начисления/выплаты на период проверки спорной активности или документов.')}</li>
            </ul>
          </section>

          <section className="p-6 md:p-8 rounded-2xl bg-gray-900/80 border border-white/10 backdrop-blur-2xl">
            <h2 className="text-xl md:text-2xl font-semibold text-white mb-4">
              {t('rulesSectionPayments', '5. Выплаты')}
            </h2>
            <ul className="list-disc list-inside space-y-2 text-neutral-300">
              <li>{t('rulesPayments1', 'Минимальная сумма для выплаты составляет $100.')}</li>
              <li>{t('rulesPayments2', 'Выплаты производятся на банковский счёт или криптокошелёк по выбору партнёра.')}</li>
              <li>{t('rulesPayments3', 'Комиссия за перевод может удерживаться из суммы выплаты.')}</li>
              <li>{t('rulesPayments4', 'Партнёр самостоятельно несёт ответственность за уплату налогов и соблюдение требований законодательства своей юрисдикции.')}</li>
              <li>{t('rulesPayments5', 'Dev48 вправе запросить подтверждающие документы/данные для выплаты и верификации личности/источника средств.')}</li>
            </ul>
          </section>
        </div>

        <div className="mt-10">
          <Link
            href={getPathWithLocale('/referral', locale)}
            className="inline-block text-green-400 hover:text-green-300 transition-colors"
          >
            {t('rulesBackToReferral', '← Вернуться к программе')}
          </Link>
        </div>
      </div>
    </main>
  );
}
